import { useCallback, useEffect, useState } from 'react';
import { getCeremonyCandidates, addCeremonyStudents } from '../../services/api';
import { getRumaalColor } from '../../utils/colors';
import { RUMAAL_RANKS } from '../../utils/constants';

// Choosing who tests.
//
// Roughly 30–40 of ~300 students test each month, grouped by rank — so the
// filters carry the screen, and anyone an instructor already signed up on the
// iPad floats to the top, since those are the ones the admin usually means.

const FIELD = 'rounded-md border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-500/15';

export default function AddStudents({ ceremonyId, onClose, onAdded }) {
  const [candidates, setCandidates] = useState([]);
  const [rank, setRank] = useState('');
  const [search, setSearch] = useState('');
  const [chosen, setChosen] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getCeremonyCandidates(ceremonyId, { rank, search });
      setCandidates(data.candidates);
    } catch (loadError) {
      setError(loadError.response?.data?.error || 'Could not load students');
    } finally {
      setLoading(false);
    }
  }, [ceremonyId, rank, search]);

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);

  const toggle = (key) => setChosen((current) => {
    const next = new Set(current);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const available = candidates.filter((c) => !c.added);

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      await addCeremonyStudents(ceremonyId, [...chosen]);
      onAdded();
    } catch (addError) {
      setError(addError.response?.data?.error || 'Could not add these students');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={busy ? undefined : onClose} />
      <div className="relative h-full w-full overflow-y-auto bg-white shadow-2xl sm:max-w-lg">
        <div className="sticky top-0 z-10 border-b border-gray-200 bg-white px-5 py-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Who's testing?</h3>
            <button onClick={onClose} className="rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-600">
              Close
            </button>
          </div>
          <div className="mt-3 flex gap-2">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search a name…"
              className={`${FIELD} flex-1`}
            />
            <select value={rank} onChange={(e) => setRank(e.target.value)} className={FIELD} aria-label="Rank">
              <option value="">Any rank</option>
              {RUMAAL_RANKS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          {available.length > 0 && (
            <button
              onClick={() => setChosen(new Set(available.map((c) => c.studentKey)))}
              className="mt-2 text-xs text-purple-600 hover:text-purple-700"
            >
              Select all {available.length} shown
            </button>
          )}
        </div>

        <div className="space-y-1.5 p-5">
          {loading && <p className="text-sm text-gray-500">Loading…</p>}
          {!loading && candidates.length === 0 && (
            <p className="text-sm text-gray-500">No students match those filters.</p>
          )}

          {candidates.map((candidate) => (
            <button
              key={candidate.studentKey}
              onClick={() => !candidate.added && toggle(candidate.studentKey)}
              disabled={candidate.added}
              className={`flex w-full items-center gap-3 rounded-lg border p-2.5 text-left transition-colors ${
                candidate.added ? 'border-gray-100 bg-gray-50 opacity-60'
                  : chosen.has(candidate.studentKey) ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 bg-white hover:bg-gray-50'
              }`}
            >
              <span
                className="h-8 w-8 shrink-0 rounded-full"
                style={{ backgroundColor: getRumaalColor(candidate.rumaalRank), boxShadow: '0 0 0 1px rgba(0,0,0,.15)' }}
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-gray-900">{candidate.name}</span>
                <span className="block truncate text-xs text-gray-500">
                  {candidate.rumaalRank || 'No rank'} → testing {candidate.testingFor || '—'} · {candidate.className}
                </span>
              </span>
              {candidate.rsvped && !candidate.added && (
                <span className="shrink-0 rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-700">
                  signed up
                </span>
              )}
              {candidate.added && (
                <span className="shrink-0 text-[11px] text-gray-500">already in</span>
              )}
            </button>
          ))}
        </div>

        {error && <p className="px-5 pb-3 text-sm text-red-600">{error}</p>}

        <div className="sticky bottom-0 flex items-center justify-between gap-3 border-t border-gray-200 bg-white px-5 py-4">
          <span className="text-sm text-gray-600">{chosen.size} chosen</span>
          <button
            onClick={submit}
            disabled={busy || chosen.size === 0}
            className="rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? 'Adding…' : `Add ${chosen.size} to the ceremony`}
          </button>
        </div>
      </div>
    </div>
  );
}
